import PropTypes from 'prop-types'
import { useEffect, useState, useRef } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import axios from 'axios'
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles'
import { Box, Link, Button, Drawer, Typography, Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Switch } from '@material-ui/core'
import { ChevronLeft, ChevronRight, Logout } from '@material-ui/icons'
// hooks
import useAuth from '../../hooks/useAuth'
import useIsMountedRef from '../../hooks/useIsMountedRef'
// routes
import { PATH_DASHBOARD, PATH_DOCS } from '../../routes/paths'
// components
import Logo from '../../components/Logo'
import MyAvatar from '../../components/MyAvatar'
import Scrollbar from '../../components/Scrollbar'
import NavSection from '../../components/NavSection'
import { MHidden } from '../../components/@material-extend'
//
import sidebarConfigRaw from './SidebarConfig'
import { DocIcon } from '../../assets'
import { VERSION, REST_API_END_POINT } from '../../constants/DefaultValues'
import { broadcastHelpDeskOnlineStatus } from '../../pages/dashboard/ChatModuleSignwise/helpDeskSession'

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280
const COLLAPSED_WIDTH = 64

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    height: '100vh',
    position: 'relative',
  },
}))

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}))

const DocStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.08)
      : theme.palette.primary.lighter,
}))

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, isCollapsed, onToggleCollapse }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const isMountedRef = useIsMountedRef()
  const { method, logout } = useAuth();
  const user = JSON.parse(localStorage.getItem('user'))
  const userType = user?.user_type
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false)
  const [isOnline, setIsOnline] = useState(false)
  const isOnlineRef = useRef(false)
  const statusFetchIdRef = useRef(0)

  const broadcastOnlineStatus = (online) => {
    broadcastHelpDeskOnlineStatus(online)
  }

  useEffect(() => {
    isOnlineRef.current = isOnline
  }, [isOnline])

  useEffect(() => {
    if (userType !== 3 && user?.user_id) {
      const requestId = ++statusFetchIdRef.current
      const fetchStatus = async () => {
        try {
          const response = await axios.post(`${REST_API_END_POINT}teams/get-user-active`, {
            userId: user.user_id
          });
          if (requestId !== statusFetchIdRef.current) return
          if (response.data.status === 1) {
            const online = response.data.data?.online === 1
            isOnlineRef.current = online
            setIsOnline(online);
            broadcastOnlineStatus(online)
          }
        } catch (error) {
          console.error('Error fetching online status:', error);
        }
      };
      fetchStatus();
    }
  }, [user?.user_id, userType]);

  const handleToggleOnline = async (event, checked) => {
    event?.stopPropagation?.()
    // Invalidate any in-flight get-user-active so it can't undo this click.
    const requestId = ++statusFetchIdRef.current
    const newStatus =
      typeof checked === 'boolean'
        ? checked
        : typeof event?.target?.checked === 'boolean'
          ? event.target.checked
          : !isOnlineRef.current
    isOnlineRef.current = newStatus
    setIsOnline(newStatus)
    broadcastOnlineStatus(newStatus)
    try {
      const response = await axios.post(`${REST_API_END_POINT}teams/update-user-active`, {
        user_id: user.user_id,
        online: newStatus ? 1 : 0
      });
      if (requestId !== statusFetchIdRef.current) return
      if (response.data.status === 1) {
        enqueueSnackbar(`Status updated to ${newStatus ? 'Online' : 'Offline'}`, { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to update status', { variant: 'error' });
        isOnlineRef.current = !newStatus
        setIsOnline(!newStatus);
        broadcastOnlineStatus(!newStatus)
      }
    } catch (error) {
      console.error('Error updating status:', error);
      if (requestId !== statusFetchIdRef.current) return
      enqueueSnackbar('Failed to update status', { variant: 'error' });
      isOnlineRef.current = !newStatus
      setIsOnline(!newStatus);
      broadcastOnlineStatus(!newStatus)
    }
  };

  // Filter config based on hideForRoles and specific user authorization
  const sidebarConfig = sidebarConfigRaw?.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => {
        if (item.hideForRoles?.includes(userType)) return false;
        if (
          item.title === 'Performance' ||
          item.title === 'User Management' ||
          item.path === PATH_DASHBOARD.performance ||
          item.path === PATH_DASHBOARD.user.root ||
          item.path === PATH_DASHBOARD.tickets.statuslogs ||
          item.path === PATH_DASHBOARD.settings ||
          item.path === PATH_DASHBOARD.modules
        ) {
          return [1, 16, 28, 39, 43].includes(user?.user_id);
        }
        return true;
      }
    ),
  }))

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
      if (isMountedRef.current) {
        onCloseSidebar();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    } finally {
      setOpenLogoutDialog(false);
    }
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Minimize/Expand Button */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={isCollapsed ? "Expand sidebar" : "Minimize sidebar"}>
          <IconButton 
            onClick={onToggleCollapse}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, mx: 2.5 }}>
        <Link
          underline="none"
          component={RouterLink}
          to={PATH_DASHBOARD.user.account}
        >
          <AccountStyle sx={{ 
            flexDirection: isCollapsed ? 'column' : 'row',
            alignItems: isCollapsed ? 'center' : 'flex-start',
            textAlign: isCollapsed ? 'center' : 'left',
            px: isCollapsed ? 1 : 2.5,
            py: isCollapsed ? 1.5 : 2
          }}>
            <MyAvatar />
            {!isCollapsed && (
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {user.person_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user.user_role}
                </Typography>
              </Box>
            )}
          </AccountStyle>
        </Link>
      </Box>

      {/* Online Status Toggle */}
      {user?.user_id == 39 && (
        <Box sx={{ px: isCollapsed ? 1 : 2.5, mb: 2 }}>
          {isCollapsed ? (
            <Tooltip title={isOnline ? "Online (Click to go offline)" : "Offline (Click to go online)"} placement="right">
              <IconButton
                onClick={handleToggleOnline}
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  display: 'flex',
                  color: isOnline ? 'success.main' : 'text.disabled',
                  bgcolor: alpha(isOnline ? '#54D62C' : '#919EAB', 0.08),
                  '&:hover': {
                    bgcolor: alpha(isOnline ? '#54D62C' : '#919EAB', 0.15),
                  }
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: isOnline ? 'success.main' : 'text.disabled',
                    boxShadow: isOnline ? '0 0 8px #54D62C' : 'none',
                    animation: isOnline ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(84, 214, 44, 0.7)' },
                      '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(84, 214, 44, 0)' },
                      '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(84, 214, 44, 0)' },
                    }
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.neutral',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: isOnline ? 'success.main' : 'text.disabled',
                    boxShadow: isOnline ? '0 0 8px #54D62C' : 'none',
                    animation: isOnline ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(84, 214, 44, 0.7)' },
                      '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(84, 214, 44, 0)' },
                      '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(84, 214, 44, 0)' },
                    }
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium', color: 'text.primary' }}>
                  {isOnline ? 'Online' : 'Offline'}
                </Typography>
              </Stack>
              <Switch
                size="small"
                checked={isOnline}
                onChange={handleToggleOnline}
                color="success"
              />
            </Box>
          )}
        </Box>
      )}

      <NavSection navConfig={sidebarConfig} isCollapsed={isCollapsed} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout Button */}
      <Box sx={{ px: 2.5, pb: 2, display: 'flex', justifyContent: 'center' }}>
        {isCollapsed ? (
          <Tooltip title="Logout" placement="right">
            <IconButton 
              onClick={handleLogoutClick}
              sx={{ 
                width: 40,
                height: 40,
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '50%',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'grey.50'
                }
              }}
            >
              <Logout sx={{ fontSize: '20px' }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Logout" placement="top">
            <Button 
              fullWidth 
              color="inherit" 
              variant="outlined" 
              onClick={handleLogoutClick}
              startIcon={<Logout />}
              sx={{ 
                borderColor: 'grey.300',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.400',
                  backgroundColor: 'grey.50'
                }
              }}
            >
              Logout
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* <Box sx={{ px: 2.5, pb: 3, mt: 2 }}>
        <DocStyle>
          <Box component="img" src={`/static/auth/customer_service.png`} sx={{ width: 36, height: 36 }} />
          <Typography gutterBottom variant="subtitle1" sx={{ color: 'grey.800' }}>
            Signwise Solutions - Client Support Portal
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Delivering reliable support, every step of the way.
            <br /> Submit tickets, track updates, and connect with our experts.
          </Typography>
        </DocStyle>
      </Box> */}

      {/* <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <DocStyle>
          <DocIcon sx={{ width: 36, height: 36, mb: 2 }} />
          <Typography gutterBottom variant="subtitle1" sx={{ color: 'grey.800' }}>
            Hi, {user.person_name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Need help?
            <br /> Please check our docs
          </Typography>
          <Button fullWidth to={PATH_DOCS.root} variant="contained" component={RouterLink}>
            Documentation
          </Button>
        </DocStyle>
      </Box> */}
    </Scrollbar>
  )

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Box
          sx={{
            width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            minWidth: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            maxWidth: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            height: '100vh',
            bgcolor: 'background.default',
            transition: 'width 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            position: 'relative'
          }}
        >
          {renderContent}
          {!isCollapsed && (
            <Stack alignItems="center" justifyContent="center">
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '14px', pb: 4, fontWeight: 'bold' }}>
                V {VERSION}
              </Typography>
            </Stack>
          )}
        </Box>
      </MHidden>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="logout-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? 
            {/* You will need to sign in again to access your account. */}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleLogoutCancel} 
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLogoutConfirm} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </RootStyle>
  )
}